import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { SignedIn, SignedOut, SignInButton } from '@clerk/clerk-react';
import './Pricing.css';

const Pricing = () => {
    const [showLoginModal, setShowLoginModal] = useState(false);

    useEffect(() => {
        const observerOptions = {
            threshold: 0.1,
            rootMargin: '0px 0px -50px 0px'
        };

        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    entry.target.style.opacity = '1';
                    entry.target.style.transform = 'translateY(0)';
                    entry.target.style.animation = 'fadeInUp 0.6s ease forwards';
                    observer.unobserve(entry.target);
                }
            });
        }, observerOptions);

        const animateElements = document.querySelectorAll(
            '.pricing-card, .section-header'
        );
        
        animateElements.forEach(el => {
            el.style.opacity = '0';
            el.style.transform = 'translateY(20px)';
            observer.observe(el);
        });

        const pricingCards = document.querySelectorAll('.pricing-card');
        pricingCards.forEach((card, index) => {
            card.style.animationDelay = `${index * 0.1}s`;
        });
    }, []);

    const handleLoginSubmit = () => {};
    
    const handlePlanSelection = async (plan, event) => {
        const button = event.currentTarget;
        const originalText = button.textContent;
        
        try {
            button.textContent = 'Processing...';
            button.disabled = true;
            
            if (plan === 'enterprise') {
                window.location.href = 'mailto:sales@huntorca.com?subject=Enterprise Plan Inquiry&body=Hi, I\'m interested in your Enterprise plan. Please provide more information.';
                button.textContent = originalText;
                button.disabled = false;
                return;
            }
            
            const customerEmail = prompt('Please enter your email address to proceed:');
            if (!customerEmail) {
                button.textContent = originalText;
                button.disabled = false;
                return;
            }
            
            const response = await fetch(`/api/stripe/create-checkout-session`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    plan: plan,
                    customerEmail: customerEmail,
                    successUrl: `${window.location.origin}/success?session_id={CHECKOUT_SESSION_ID}`,
                    cancelUrl: `${window.location.origin}/pricing`
                })
            });
            
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to create checkout session');
            }
            
            const { url } = await response.json();
            window.location.href = url;
            
        } catch (error) {
            console.error('Error:', error);
            alert(error.message || 'Sorry, something went wrong. Please try again.');
            button.textContent = originalText;
            button.disabled = false;
        }
    };

    return (
        <div className="container">
            <nav className="navbar">
                <Link to="/" className="navbar-logo">
                    <img src="/huntorca-logo.png" alt="HuntOrca Logo" className="logo-img" />
                    <h1>HuntOrca</h1>
                </Link>
                <div className="navbar-links">
                    <Link to="/" className="navbar-link">Home</Link>
                    <Link to="/pricing" className="navbar-link active">Pricing</Link>
                    <Link to="/solutions" className="navbar-link">Solutions</Link>
                    <Link to="/data-methodology" className="navbar-link">Data & Methodology</Link>
                    <SignedIn>
                        <Link to="/dashboard" className="navbar-button">Dashboard</Link>
                    </SignedIn>
                    <SignedOut>
                        <SignInButton
                            // Use new redirect props
                            signInForceRedirectUrl="/dashboard"
                            signUpForceRedirectUrl="/dashboard"
                        >
                            <button className="navbar-button" type="button">Login</button>
                        </SignInButton>
                    </SignedOut>
                </div>
            </nav>

            <main>
                <section className="pricing-section">
                    <div className="section-header">
                        <h2 className="section-title">Built for California, Priced for Growth</h2>
                        <p className="section-subtitle">Unlock the deepest well of California solar data. Choose the plan that fits your team's needs.</p>
                    </div>

                    <div className="pricing-grid">
                        <div className="pricing-card">
                            <h3 className="plan-name">Professional</h3>
                            <p className="plan-description">For sales professionals who need to find and qualify high-potential installers.</p>
                            <div className="plan-price">$99 <span className="period">/ user / month</span></div>
                            <button className="plan-button secondary" onClick={(e) => handlePlanSelection('professional', e)}>Choose Professional</button>
                            
                            <div className="features-group">
                                <h4>Performance Metrics</h4>
                                <ul className="plan-features">
                                    <li><svg className="icon" viewBox="0 0 24 24"><path d="M5 13l4 4L19 7" /></svg> Full California Database Access</li>
                                    <li><svg className="icon" viewBox="0 0 24 24"><path d="M5 13l4 4L19 7" /></svg> Advanced Search & Filtering</li>
                                    <li><svg className="icon" viewBox="0 0 24 24"><path d="M5 13l4 4L19 7" /></svg> Installation Volume & Velocity</li>
                                    <li><svg className="icon" viewBox="0 0 24 24"><path d="M5 13l4 4L19 7" /></svg> 1,000 Data Exports / month</li>
                                    <li><svg className="icon" viewBox="0 0 24 24"><path d="M5 13l4 4L19 7" /></svg> Email & Chat Support</li>
                                </ul>
                            </div>
                        </div>
                        
                        <div className="pricing-card highlight">
                            <div className="highlight-badge">Most Popular</div>
                            <h3 className="plan-name">Business</h3>
                            <p className="plan-description">For teams that need deeper insights and CRM integration to scale their outreach.</p>
                            <div className="plan-price">$249 <span className="period">/ user / month</span></div>
                            <button className="plan-button" onClick={(e) => handlePlanSelection('business', e)}>Choose Business</button>
                            
                            <div className="features-group">
                                <h4>Everything in Professional, plus:</h4>
                                <ul className="plan-features">
                                    <li><svg className="icon" viewBox="0 0 24 24"><path d="M5 13l4 4L19 7" /></svg> 5,000 Data Exports / month</li>
                                    <li><svg className="icon" viewBox="0 0 24 24"><path d="M5 13l4 4L19 7" /></svg> CRM Integration (Salesforce, HubSpot)</li>
                                    <li><svg className="icon" viewBox="0 0 24 24"><path d="M5 13l4 4L19 7" /></svg> Team Management & Reporting</li>
                                    <li><svg className="icon" viewBox="0 0 24 24"><path d="M5 13l4 4L19 7" /></svg> Priority Support</li>
                                </ul>
                            </div>

                            <div className="features-group premium">
                                <h4>Advanced Analytics & Scores</h4>
                                <ul className="plan-features">
                                    <li><svg className="icon" viewBox="0 0 24 24"><path d="M5 13l4 4L19 7" /></svg> Installer Efficiency Score</li>
                                    <li><svg className="icon" viewBox="0 0 24 24"><path d="M5 13l4 4L19 7" /></svg> Installer Risk Score</li>
                                    <li><svg className="icon" viewBox="0 0 24 24"><path d="M5 13l4 4L19 7" /></svg> Cost Competitiveness Score</li>
                                    <li><svg className="icon" viewBox="0 0 24 24"><path d="M5 13l4 4L19 7" /></svg> Storage Adoption Rate Tracking</li>
                                </ul>
                            </div>
                        </div>

                        <div className="pricing-card">
                            <h3 className="plan-name">Enterprise</h3>
                            <p className="plan-description">For large organizations requiring custom data solutions and full API access.</p>
                            <div className="plan-price">Custom</div>
                            <button className="plan-button secondary" onClick={(e) => handlePlanSelection('enterprise', e)}>Contact Sales</button>

                             <div className="features-group">
                                <h4>Everything in Business, plus:</h4>
                                <ul className="plan-features">
                                    <li><svg className="icon" viewBox="0 0 24 24"><path d="M5 13l4 4L19 7" /></svg> Unlimited Data Exports</li>
                                    <li><svg className="icon" viewBox="0 0 24 24"><path d="M5 13l4 4L19 7" /></svg> Full API Access</li>
                                    <li><svg className="icon" viewBox="0 0 24 24"><path d="M5 13l4 4L19 7" /></svg> Custom Data Feeds & Integrations</li>
                                    <li><svg className="icon" viewBox="0 0 24 24"><path d="M5 13l4 4L19 7" /></svg> Dedicated Account Manager</li>
                                    <li><svg className="icon" viewBox="0 0 24 24"><path d="M5 13l4 4L19 7" /></svg> SAML / SSO Integration</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </section>
            </main>

            <footer className="footer">
                <p>&copy; {new Date().getFullYear()} HuntOrca. All rights reserved.</p>
                <p>Email: <a href="mailto:info@huntorca.com">info@huntorca.com</a></p>
            </footer>

            {showLoginModal && (
                 <div className="modal-backdrop" onClick={() => setShowLoginModal(false)}>
                    <div className="modal-content" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>Login to Your Account</h2>
                            <button className="modal-close" onClick={() => setShowLoginModal(false)}>&times;</button>
                        </div>
                        <form onSubmit={handleLoginSubmit} className="modal-form">
                            <div className="form-group">
                                <label htmlFor="login-email">Email Address</label>
                                <input id="login-email" name="email" type="email" required />
                            </div>
                            <div className="form-group">
                                <label htmlFor="login-password">Password</label>
                                <input id="login-password" name="password" type="password" required />
                            </div>
                            {/* Error state removed with Clerk sign-in */}
                            <button type="submit" className="form-button">Login</button>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Pricing; 